import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./LandingPage.css";

const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

// ─── Style tokens (Closvio palette: navy / white / gold) ──────────────────────
const NAVY   = "#0B1F3A";
const GOLD   = "#C9A227";
const BG     = "#F8FAFC";
const WHITE  = "#FFFFFF";
const BORDER = "#E2E8F0";
const MUTED  = "#64748B";
const SERIF  = "Georgia, 'Times New Roman', serif";
const SANS   = "Inter, -apple-system, sans-serif";

const s = {
    sectionLabel: {
        color: GOLD, fontSize: "11px", fontWeight: 600,
        letterSpacing: "0.1em", textTransform: "uppercase",
        textAlign: "center", marginBottom: "16px",
    },
    sectionHead: {
        color: NAVY, fontSize: "36px", fontFamily: SERIF,
        fontWeight: "normal", textAlign: "center",
    },
    wrap: { width: "100%", padding: "0 48px", boxSizing: "border-box" },
};

// ─── Content: the three services ──────────────────────────────────────────────
const MODULES = [
    {
        id: "deal-management",
        tag: "Module 1",
        name: "Deal Management",
        line: "Run every mandate from instruction to completion.",
        blurb:
            "The core of the practice: pipeline, buyers, documents, compliance and fees in one place, with AI watching for deals that are slipping.",
        features: [
            "Pipeline and kanban board across every stage",
            "Deal Health Score, 0–100, for every active mandate",
            "Revenue Leakage Detection, shown in GBP",
            "Mandate matching engine scores your whole buyer database",
            "Daily AI briefing: what is at risk and what to do first",
            "Conflict-of-interest checks that warn without blocking progress",
            "AML compliance module",
            "EBITDA normalisation and valuation",
            "Data room with access controls",
            "PDF fee schedule export",
        ],
    },
    {
        id: "sourcing",
        tag: "Module 2",
        name: "Sourcing",
        line: "Find the businesses worth approaching, before anyone else does.",
        blurb:
            "Search the whole UK Companies House register, see who owns each company and how old they are, and get a turnover and enterprise value estimate with the reasoning shown.",
        features: [
            "Target search across the UK Companies House register",
            "Owner (PSC) ages and ownership structure to spot succession candidates",
            "Target Analysis: turnover and EV ranges with confidence tier and explanation",
            "Sector benchmarks and multiples, with SIC code as a hard pre-filter",
            "Mandate runs that rescore targets against your criteria",
            "Candidate pipeline from shortlist to approached to mandated",
            "Convert a target to a deal in one click, with its origin carried across",
        ],
    },
    {
        id: "distressed",
        tag: "Module 3",
        name: "Distressed",
        line: "Spot trouble early and manage turnaround and restructuring mandates.",
        blurb:
            "Signals, diagnosis and cash tools for advisers working with businesses under pressure, from first warning sign through to a funded plan.",
        features: [
            "Distress signal sourcing and a company watchlist",
            "Diagnosis toolkit for stressed businesses",
            "TUPE calculator for employee transfer costs",
            "13-week cash flow builder with funding gap, facility headroom and breach week",
            "Cash flow seeded from the latest filed accounts, with assumptions labelled",
            "Distressed mandate workflow",
            "Convert a distressed mandate into a managed deal",
        ],
    },
];

const FLOW = [
    { step: "1", title: "Find", body: "Sourcing surfaces targets and distressed situations." },
    { step: "2", title: "Qualify", body: "Target Analysis, owner ages, diagnosis and cash flow." },
    { step: "3", title: "Convert", body: "One click creates a deal, with its origin and figures attached." },
    { step: "4", title: "Close", body: "Deal Management runs it to completion and tracks the fee." },
];

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
    const [demoOpen, setDemoOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();

    const openDemo  = () => { setSubmitted(false); setDemoOpen(true); };
    const closeDemo = () => { setDemoOpen(false); setSubmitted(false); };

    const submitDemo = async (e) => {
        e.preventDefault();
        const form = e.target;
        try {
            await fetch("/api/waitlist", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email.value,
                    name: form.name.value,
                    firm: form.firm?.value || "",
                    firm_type: form["firm-type"]?.value || "",
                    source: "book-demo-landing",
                }),
            });
        } catch { /* fail silently */ }
        setSubmitted(true);
        setTimeout(() => { closeDemo(); navigate("/register"); }, 2000);
    };

    const navLink = { color: WHITE, fontSize: "14px", fontWeight: 400, textDecoration: "none" };

    return (
        <div style={{ fontFamily: SANS, background: BG, color: NAVY, minHeight: "100vh" }}>

            {/* ── NAVIGATION ── */}
            <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 50, height: "64px", background: NAVY }}>
                <div style={{ ...s.wrap, height: "100%", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center" }}>
                    <span style={{ color: WHITE, fontSize: "20px", fontWeight: 600 }}>
                        Clos<span style={{ color: GOLD }}>vio</span>
                    </span>
                    <div className="lp-nav-links" style={{ display: "flex", alignItems: "center", gap: "32px" }}>
                        <a href="#services" onClick={(e) => { e.preventDefault(); scrollTo("services"); }} style={navLink}>Services</a>
                        <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo("how-it-works"); }} style={navLink}>How it works</a>
                        <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo("pricing"); }} style={navLink}>Pricing</a>
                        <Link to="/login" style={navLink}>Sign In</Link>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                            onClick={openDemo}
                            data-testid="nav-book-demo-btn"
                            style={{ background: GOLD, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "8px 20px", border: "none", cursor: "pointer" }}
                        >Book a Demo</button>
                    </div>
                </div>
            </nav>

            {/* ── HERO ── */}
            <section style={{ background: NAVY, paddingTop: "168px", paddingBottom: "96px" }}>
                <div className="lp-hero-grid" style={{ ...s.wrap, display: "grid", gridTemplateColumns: "55% 45%", gap: "48px", alignItems: "center" }}>
                    <div>
                        <p style={{ color: GOLD, fontSize: "11px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "24px" }}>
                            AI Operating System for M&A Firms
                        </p>
                        <div>
                            <div className="lp-hero-h" style={{ color: WHITE, fontSize: "60px", fontFamily: SERIF, fontWeight: "normal", lineHeight: 1.1 }}>
                                Find the deal.
                            </div>
                            <div className="lp-hero-h" style={{ color: GOLD, fontSize: "60px", fontFamily: SERIF, fontStyle: "italic", fontWeight: "normal", lineHeight: 1.1 }}>
                                Run it to completion.
                            </div>
                        </div>
                        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "17px", lineHeight: 1.6, marginTop: "24px", maxWidth: "620px" }}>
                            Closvio gives UK M&A brokers and boutique advisory firms one platform for the whole job: <strong style={{ color: WHITE, fontWeight: 600 }}>Sourcing</strong> to
                            find and value targets, <strong style={{ color: WHITE, fontWeight: 600 }}>Deal Management</strong> to run mandates through to completion,
                            and <strong style={{ color: WHITE, fontWeight: 600 }}>Distressed</strong> for turnaround and restructuring work. One login, one record of every deal.
                        </p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "24px" }}>
                            {["Sourcing", "Deal Management", "Distressed"].map((m) => (
                                <span key={m} style={{ border: "1px solid rgba(201,162,39,0.6)", color: GOLD, fontSize: "12px", fontWeight: 600, borderRadius: "999px", padding: "5px 14px" }}>{m}</span>
                            ))}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "16px", marginTop: "36px" }}>
                            <button
                                onClick={openDemo}
                                data-testid="hero-book-demo-btn"
                                style={{ background: GOLD, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 28px", border: "none", cursor: "pointer" }}
                            >Book a Demo</button>
                            <Link
                                to="/register"
                                data-testid="hero-free-trial-btn"
                                style={{ background: "transparent", color: WHITE, fontSize: "13px", fontWeight: 500, borderRadius: "6px", padding: "12px 28px", border: "1px solid rgba(255,255,255,0.5)", textDecoration: "none", display: "inline-block" }}
                            >Start Free</Link>
                        </div>
                        <p style={{ color: MUTED, fontSize: "13px", marginTop: "16px" }}>
                            No setup fee. No contract. Cancel anytime.
                        </p>
                    </div>

                    {/* Illustrative product panel: one insight per service */}
                    <div className="lp-hero-mockup" data-testid="hero-mockup" style={{ background: "#1E293B", borderRadius: "8px", padding: "24px", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 0 60px rgba(201,162,39,0.08)" }}>
                        <div style={{ color: MUTED, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px" }}>
                            Illustrative · one platform, three services
                        </div>
                        {[
                            { k: "SOURCING", c: GOLD, t: "Harlow Precision Ltd", d: "Owner 67, no succession signal filed. Est. turnover £4.1–5.3M, EV £3.2–4.4M (Medium confidence)." },
                            { k: "DEAL MANAGEMENT", c: "#22C55E", t: "Meridian Care Group", d: "Deal Health 82. IOI expected within 7 days. 3 matched buyers not yet contacted." },
                            { k: "DISTRESSED", c: "#EF4444", t: "Thornfield Engineering", d: "13-week forecast breaches facility in week 7. Peak funding need £310k." },
                        ].map(({ k, c, t, d }) => (
                            <div key={k} style={{ borderLeft: `3px solid ${c}`, background: "rgba(255,255,255,0.04)", borderRadius: "4px", padding: "12px 14px", marginBottom: "10px" }}>
                                <div style={{ color: c, fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em", marginBottom: "4px" }}>{k}</div>
                                <div style={{ color: WHITE, fontSize: "13px", fontWeight: 600, marginBottom: "2px" }}>{t}</div>
                                <div style={{ color: "#94A3B8", fontSize: "12px", lineHeight: 1.5 }}>{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PRACTITIONER BAR ── */}
            <div style={{ background: WHITE, padding: "24px 0", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, textAlign: "center" }}>
                <p style={{ color: MUTED, fontSize: "14px", maxWidth: "760px", margin: "0 auto", padding: "0 48px" }}>
                    Built by an M&A practitioner with 25 years of senior finance leadership experience across transactions, advisory, and corporate finance.
                </p>
            </div>

            {/* ── THE PROBLEM ── */}
            <section style={{ background: BG, padding: "80px 0" }}>
                <div style={s.wrap}>
                    <p style={s.sectionLabel}>The Problem</p>
                    <h2 style={{ ...s.sectionHead, maxWidth: "760px", margin: "0 auto 16px" }}>
                        Origination, execution and distressed work usually live in different tools, or none.
                    </h2>
                    <p style={{ color: MUTED, fontSize: "18px", textAlign: "center", marginBottom: "64px" }}>
                        Closvio puts them in one place.
                    </p>
                    <div className="lp-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "32px" }}>
                        {[
                            { n: "01", h: "Good targets never get approached", b: "Finding sellers means trawling registers and guessing at size and ownership. Most firms approach the same few names, or none at all." },
                            { n: "02", h: "Deals go cold and buyers go uncontacted", b: "Without visibility into deal health, you discover a mandate is dying when the seller calls. Matched buyers sit uncontacted for weeks." },
                            { n: "03", h: "Distress is spotted too late", b: "Troubled businesses need fast diagnosis and a credible cash plan. Doing it in spreadsheets costs time you do not have and fees you could have won." },
                        ].map(({ n, h, b }) => (
                            <div key={n}>
                                <div style={{ color: GOLD, fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>{n}</div>
                                <h3 style={{ color: NAVY, fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>{h}</h3>
                                <p style={{ color: MUTED, fontSize: "14px", lineHeight: 1.6 }}>{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── THE THREE SERVICES ── */}
            <section id="services" style={{ background: WHITE, padding: "80px 0" }}>
                <div style={s.wrap}>
                    <p style={s.sectionLabel}>The Platform</p>
                    <h2 style={{ ...s.sectionHead, maxWidth: "720px", margin: "0 auto 16px" }}>
                        Three services. One login. One record of every deal.
                    </h2>
                    <p style={{ color: MUTED, fontSize: "16px", textAlign: "center", marginBottom: "64px" }}>
                        Use one, or all three. Each works on its own and they connect when you need them to.
                    </p>
                    <div className="lp-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", alignItems: "stretch" }}>
                        {MODULES.map((m) => (
                            <div key={m.id} id={m.id} data-testid={`module-${m.id}`} style={{ background: BG, border: `1px solid ${BORDER}`, borderTop: `3px solid ${GOLD}`, borderRadius: "6px", padding: "32px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column" }}>
                                <div style={{ color: GOLD, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, marginBottom: "10px" }}>{m.tag}</div>
                                <h3 style={{ color: NAVY, fontSize: "26px", fontFamily: SERIF, fontWeight: "normal", marginBottom: "8px" }}>{m.name}</h3>
                                <p style={{ color: NAVY, fontSize: "15px", fontWeight: 600, lineHeight: 1.4, marginBottom: "12px" }}>{m.line}</p>
                                <p style={{ color: MUTED, fontSize: "14px", lineHeight: 1.6, marginBottom: "20px" }}>{m.blurb}</p>
                                <div style={{ height: "1px", background: BORDER, marginBottom: "16px" }} />
                                <ul style={{ listStyle: "none", padding: 0, margin: 0, flex: 1 }}>
                                    {m.features.map((f) => (
                                        <li key={f} style={{ fontSize: "13px", color: NAVY, lineHeight: 1.6, display: "flex", alignItems: "flex-start", marginBottom: "8px" }}>
                                            <span style={{ color: GOLD, marginRight: "8px", fontWeight: 600, flexShrink: 0 }}>✓</span>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── HOW IT FITS TOGETHER ── */}
            <section id="how-it-works" style={{ background: NAVY, padding: "80px 0" }}>
                <div style={s.wrap}>
                    <p style={s.sectionLabel}>How it fits together</p>
                    <h2 style={{ ...s.sectionHead, color: WHITE, maxWidth: "720px", margin: "0 auto 16px" }}>
                        From first signal to completed deal, nothing is re-keyed.
                    </h2>
                    <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "16px", textAlign: "center", maxWidth: "720px", margin: "0 auto 56px", lineHeight: 1.6 }}>
                        A target from Sourcing or a mandate from Distressed converts into a deal in one click. Its financial estimates, owner details, distress diagnosis and cash flow context go with it, and conflict-of-interest overlaps appear as a warning instead of a roadblock.
                    </p>
                    <div className="lp-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "24px" }}>
                        {FLOW.map(({ step, title, body }) => (
                            <div key={step} style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", padding: "24px" }}>
                                <div style={{ color: GOLD, fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>STEP {step}</div>
                                <div style={{ color: WHITE, fontSize: "20px", fontFamily: SERIF, marginBottom: "8px" }}>{title}</div>
                                <p style={{ color: "#94A3B8", fontSize: "14px", lineHeight: 1.6 }}>{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── WHO IT IS FOR ── */}
            <section style={{ background: BG, padding: "80px 0" }}>
                <div style={s.wrap}>
                    <p style={s.sectionLabel}>Who it is for</p>
                    <h2 style={{ ...s.sectionHead, maxWidth: "720px", margin: "0 auto 56px" }}>
                        Built for the way UK advisory firms actually work.
                    </h2>
                    <div className="lp-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px" }}>
                        {[
                            { h: "M&A brokers and boutiques", b: "Originate with Sourcing, run mandates in Deal Management, and see where every fee is coming from." },
                            { h: "Acquirers and search funds", b: "Use Sourcing to build a ranked list of acquisition targets with owner ages and valuation ranges, then manage approaches through to a deal." },
                            { h: "Turnaround and restructuring advisers", b: "Use Distressed to diagnose, forecast cash and manage mandates, then carry the work into Deal Management when a transaction follows." },
                        ].map(({ h, b }) => (
                            <div key={h} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "6px", padding: "28px" }}>
                                <h3 style={{ color: NAVY, fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>{h}</h3>
                                <p style={{ color: MUTED, fontSize: "14px", lineHeight: 1.6 }}>{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PRICING ── */}
            <section id="pricing" style={{ background: WHITE, padding: "80px 0" }}>
                <div style={s.wrap}>
                    <p style={s.sectionLabel}>Pricing</p>
                    <h2 style={{ ...s.sectionHead, marginBottom: "8px" }}>
                        Start free. Add Sourcing and Distressed when you need them.
                    </h2>
                    <p style={{ color: MUTED, fontSize: "16px", textAlign: "center", marginBottom: "56px" }}>
                        One simple price per firm size, not per seat. Founding-customer pricing is available during the beta.
                    </p>
                    <div className="lp-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "24px", alignItems: "stretch" }}>
                        <PlanCard
                            name="Free"
                            headline="£0"
                            desc="Try Deal Management with no card."
                            features={["Deal Management", "Up to 2 users and 10 active deals", "Pipeline, data room and EBITDA normalisation"]}
                            cta={<Link to="/register" data-testid="pricing-free-cta" style={{ display: "block", textAlign: "center", border: `1px solid ${NAVY}`, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 24px", textDecoration: "none" }}>Start Free</Link>}
                        />
                        <PlanCard
                            name="Deal Management + Sourcing"
                            headline="Solo to Team"
                            desc="For brokers and boutiques who originate and execute."
                            features={["Deal Management and Sourcing", "Unlimited deals", "Sized by team: one user up to five", "Target Analysis and convert-to-deal"]}
                            featured
                            cta={<button onClick={openDemo} data-testid="pricing-core-cta" style={{ background: GOLD, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 24px", border: "none", cursor: "pointer", width: "100%" }}>Book a Demo for pricing</button>}
                        />
                        <PlanCard
                            name="Sourcing only"
                            headline="Targets"
                            desc="For acquirers and search funds who need targets, not deal management."
                            features={["Sourcing", "Owner ages and valuation ranges", "Candidate pipeline", "Upgrade later with all data kept"]}
                            cta={<button onClick={openDemo} data-testid="pricing-sourcing-cta" style={{ background: NAVY, color: WHITE, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 24px", border: "none", cursor: "pointer", width: "100%" }}>Book a Demo for pricing</button>}
                        />
                        <PlanCard
                            name="Firm + Distressed"
                            headline="Everything"
                            desc="Up to 15 users with all three services."
                            features={["Deal Management, Sourcing and Distressed", "Up to 15 users", "Priority support and onboarding call", "Distressed can also be added to a smaller plan"]}
                            cta={<button onClick={openDemo} data-testid="pricing-firm-cta" style={{ background: NAVY, color: WHITE, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 24px", border: "none", cursor: "pointer", width: "100%" }}>Book a Demo for pricing</button>}
                        />
                    </div>
                    <p style={{ color: MUTED, fontSize: "13px", textAlign: "center", marginTop: "24px" }}>
                        All prices exclude VAT. Confirmed plan prices are shared in your demo.
                    </p>
                </div>
            </section>

            {/* ── FINAL CTA ── */}
            <section style={{ background: NAVY, padding: "80px 0", textAlign: "center" }}>
                <div style={s.wrap}>
                    <h2 style={{ color: WHITE, fontSize: "36px", fontFamily: SERIF, fontWeight: "normal", marginBottom: "16px" }}>
                        Ready to run your practice properly?
                    </h2>
                    <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "16px", maxWidth: "600px", margin: "0 auto 40px", lineHeight: 1.6 }}>
                        Source targets, manage mandates and handle distressed work in one platform built for UK M&A firms.
                    </p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: "16px" }}>
                        <button
                            onClick={openDemo}
                            data-testid="cta-book-demo-btn"
                            style={{ background: GOLD, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 28px", border: "none", cursor: "pointer" }}
                        >Book a Demo</button>
                        <Link
                            to="/register"
                            data-testid="cta-free-trial-btn"
                            style={{ background: "transparent", color: WHITE, fontSize: "13px", fontWeight: 500, borderRadius: "6px", padding: "12px 28px", border: "1px solid rgba(255,255,255,0.3)", textDecoration: "none" }}
                        >Start Free</Link>
                    </div>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer style={{ background: NAVY, padding: "40px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ ...s.wrap, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "24px" }}>
                    <div>
                        <div style={{ color: WHITE, fontSize: "15px", fontWeight: 600, marginBottom: "6px" }}>
                            Clos<span style={{ color: GOLD }}>vio</span>
                        </div>
                        <p style={{ color: MUTED, fontSize: "13px", marginBottom: "6px" }}>Deal Management · Sourcing · Distressed</p>
                        <p style={{ color: "#475569", fontSize: "12px" }}>© 2026 Closvio. All rights reserved.</p>
                    </div>
                    <div style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
                        <a href="#" style={{ color: WHITE, fontSize: "13px", textDecoration: "none" }}>Privacy Policy</a>
                        <a href="#" style={{ color: WHITE, fontSize: "13px", textDecoration: "none" }}>Terms of Service</a>
                        <Link to="/login" style={{ color: WHITE, fontSize: "13px", textDecoration: "none" }}>Sign In</Link>
                        <a href="mailto:hello@closvio.com" style={{ color: WHITE, fontSize: "13px", textDecoration: "none" }}>Contact</a>
                    </div>
                </div>
            </footer>

            {/* ── DEMO MODAL ── */}
            {demoOpen && (
                <div
                    onClick={closeDemo}
                    data-testid="demo-modal-overlay"
                    style={{ position: "fixed", inset: 0, background: "rgba(11,31,58,0.72)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid="demo-modal"
                        style={{ background: WHITE, borderRadius: "6px", padding: "40px", maxWidth: "480px", width: "100%", margin: "0 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
                    >
                        {submitted ? (
                            <div style={{ textAlign: "center", padding: "20px 0" }}>
                                <div style={{ color: "#15803D", fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>You're on the list.</div>
                                <p style={{ color: MUTED, fontSize: "14px" }}>Redirecting to registration…</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                                    <h2 style={{ color: NAVY, fontSize: "20px", fontWeight: 600, fontFamily: SANS }}>Book a Demo</h2>
                                    <button onClick={closeDemo} style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, fontSize: "22px", lineHeight: 1, padding: "0 4px" }}>×</button>
                                </div>
                                <p style={{ color: MUTED, fontSize: "14px", marginBottom: "24px" }}>
                                    Tell us about your firm and we'll be in touch within 24 hours.
                                </p>
                                <form onSubmit={submitDemo} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                        <ModalField label="Full Name" name="name" required />
                                        <ModalField label="Email" name="email" type="email" required />
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                        <ModalField label="Firm Name" name="firm" />
                                        <div>
                                            <label style={{ display: "block", color: MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>Firm Type</label>
                                            <select name="firm-type" style={{ width: "100%", border: `1px solid ${BORDER}`, borderRadius: "6px", padding: "8px 12px", fontSize: "14px", outline: "none", background: WHITE, boxSizing: "border-box", color: NAVY }}>
                                                <option value="">Select…</option>
                                                <option value="ma_broker">M&A Broker</option>
                                                <option value="boutique_advisory">Boutique Advisory</option>
                                                <option value="corporate_finance">Corporate Finance</option>
                                                <option value="acquirer">Acquirer / Search Fund</option>
                                                <option value="turnaround">Turnaround / Restructuring</option>
                                                <option value="pe_fund">PE / VC Fund</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button type="submit" data-testid="demo-submit-btn" style={{ background: GOLD, color: NAVY, fontSize: "13px", fontWeight: 600, borderRadius: "6px", padding: "12px 24px", border: "none", cursor: "pointer", marginTop: "8px" }}>
                                        Request a Demo
                                    </button>
                                </form>
                                <p style={{ color: "#94A3B8", fontSize: "12px", textAlign: "center", marginTop: "16px" }}>
                                    No commitment. Typically respond within one business day.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Plan card ────────────────────────────────────────────────────────────────
function PlanCard({ name, headline, desc, features, cta, featured }) {
    return (
        <div style={{
            background: WHITE,
            border: featured ? `2px solid ${GOLD}` : `1px solid ${BORDER}`,
            borderRadius: "6px", padding: "28px",
            display: "flex", flexDirection: "column", position: "relative",
        }}>
            {featured && (
                <span style={{ position: "absolute", top: "-13px", left: "50%", transform: "translateX(-50%)", background: GOLD, color: NAVY, fontSize: "11px", fontWeight: 600, borderRadius: "4px", padding: "3px 12px", whiteSpace: "nowrap" }}>
                    MOST POPULAR
                </span>
            )}
            <p style={{ color: MUTED, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, marginBottom: "12px" }}>{name}</p>
            <div style={{ color: NAVY, fontSize: "32px", fontFamily: SERIF, marginBottom: "8px" }}>{headline}</div>
            <p style={{ color: MUTED, fontSize: "13px", lineHeight: 1.5, marginBottom: "20px" }}>{desc}</p>
            <div style={{ height: "1px", background: BORDER, marginBottom: "20px" }} />
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", flex: 1 }}>
                {features.map((f) => (
                    <li key={f} style={{ fontSize: "14px", color: NAVY, lineHeight: 1.6, display: "flex", alignItems: "flex-start", marginBottom: "6px" }}>
                        <span style={{ color: GOLD, marginRight: "8px", fontWeight: 600, flexShrink: 0 }}>✓</span>
                        {f}
                    </li>
                ))}
            </ul>
            {cta}
        </div>
    );
}

// ─── Modal form field ─────────────────────────────────────────────────────────
function ModalField({ label, name, type = "text", required }) {
    return (
        <div>
            <label style={{ display: "block", color: MUTED, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>{label}</label>
            <input
                name={name}
                type={type}
                required={required}
                style={{ width: "100%", border: `1px solid ${BORDER}`, borderRadius: "6px", padding: "8px 12px", fontSize: "14px", outline: "none", boxSizing: "border-box", color: NAVY }}
            />
        </div>
    );
}
